/**
 * React bindings for the Audio DAW.
 *
 * The library itself is framework-agnostic: it draws a canvas and exposes every
 * control as a method. These hooks do three things React needs and the core
 * deliberately doesn't do — own the instance lifecycle, keep host callbacks from
 * going stale, and mirror the DAW's push notifications into React state.
 *
 * @requires react ^16.8.0 || ^17.0.0 || ^18.0.0 || ^19.0.0
 */

import { useEffect, useRef, useState, useCallback } from "react";
import AudioDAW from "./index.js";

/**
 * Every `on*` callback `AudioDAW` accepts.
 *
 * Kept as data rather than spelled out at each call site because it's the list
 * that goes stale: a callback added to the core and not added here is silently
 * never delivered to a React host. A test asserts this matches the constructor.
 */
export const DAW_CALLBACKS = [
  "onLoadingStateChange",
  "onPlayStateChange",
  "onTimeUpdate",
  "onSelectionChange",
  "onRegionsChange",
  "onRegionsDrag",
  "onTrackAdded",
  "onTrackRemoved",
  "onTrackOffsetChange",
  "onTrackVolumeChange",
  "onTrackPanChange",
  "onTrackGainChange",
  "onTrackCompressorChange",
  "onTrackMuteChange",
  "onTrackSoloChange",
  "onTrackLayoutChange",
  "onTrackFrequencyRangeChange",
  "onFrequencyHighlightsChange",
  "onTrackColorChange",
  "onTrackSpectrogramOpacityChange",
  "onTrackSpectrogramVariantChange",
  "onPlaybackSpeedChange",
  "onInterpolatorChange",
  "onPitchPreservationChange",
  "onInteractionModeChange",
  "onTrackReplaced",
  "onZoomChange",
  "onDurationChange",
  "onScrollChange",
  "onActiveRegionGroupChange",
  "onRegionGroupsChange",
  "onRegionDisplayModeChange",
];

/**
 * Create and own an `AudioDAW` bound to a canvas.
 *
 * Two things here are load-bearing, and both were wrong in the first version of
 * these bindings:
 *
 * - **The instance is React state, not a ref.** Mutating a ref doesn't
 *   re-render, so a hook that returned `ref.current` handed the component `null`
 *   on first render and never told it otherwise. Anything that only re-rendered
 *   when the DAW itself reported something could never start, because the host
 *   had no instance to call.
 * - **Callbacks are dispatched through a ref.** The DAW stores the callbacks it
 *   is constructed with, for its whole life. Passing them directly would freeze
 *   the first render's closures — the classic symptom being a handler that reads
 *   the initial value of a `useState` forever. Stable dispatchers that forward
 *   to the latest props avoid it, and mean the options object doesn't have to be
 *   memoised by the caller.
 *
 * @param {React.RefObject<HTMLCanvasElement>} canvasRef - main timeline canvas
 * @param {Object} [options] - `AudioDAW` options, plus:
 * @param {React.RefObject<HTMLCanvasElement>} [options.minimapCanvasRef] - optional minimap canvas
 * @returns {AudioDAW|null} the instance, or null until the canvas is mounted
 *
 * @example
 * function Editor() {
 *   const canvasRef = useRef(null);
 *   const daw = useAudioDAW(canvasRef, {
 *     pixelsPerSecond: 100,
 *     interactionMode: "annotate",
 *     onTrackAdded: (id) => console.log("added", id),
 *   });
 *
 *   return (
 *     <>
 *       <canvas ref={canvasRef} width={1000} height={400} />
 *       <button onClick={() => daw?.play()} disabled={!daw}>Play</button>
 *     </>
 *   );
 * }
 */
export function useAudioDAW(canvasRef, options = {}) {
  const [daw, setDaw] = useState(null);
  const optionsRef = useRef(options);

  // No dependency array: the newest props must be in place before any callback
  // the DAW fires after this commit.
  useEffect(() => {
    optionsRef.current = options;
  });

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return undefined;

    const initial = optionsRef.current || {};
    const config = { ...initial };
    delete config.minimapCanvasRef;

    if (initial.minimapCanvasRef?.current) {
      config.minimapCanvas = initial.minimapCanvasRef.current;
    }

    for (const name of DAW_CALLBACKS) {
      config[name] = (...args) => optionsRef.current?.[name]?.(...args);
    }

    const instance = new AudioDAW(canvas, config);
    setDaw(instance);

    return () => {
      instance.destroy();
      setDaw(null);
    };
    // Rebuilding on every option change would tear down the audio graph and lose
    // the playhead; the ref above is what keeps options live instead.
  }, [canvasRef]);

  return daw;
}

/**
 * `useAudioDAW` plus the DAW's push notifications mirrored into React state.
 *
 * Only values the DAW actually reports are here. Things it never announces —
 * buffering, stall state — are polled by `useAudioDAWBuffering` instead, so that
 * the cost is opt-in and nothing here pretends to be reactive when it isn't.
 *
 * @param {React.RefObject<HTMLCanvasElement>} canvasRef
 * @param {Object} [options] - same as `useAudioDAW`
 * @returns {{
 *   daw: AudioDAW|null,
 *   tracks: Array,
 *   isPlaying: boolean,
 *   currentTime: number,
 *   duration: number,
 *   playbackSpeed: number,
 *   zoom: number,
 *   scrollTime: number,
 *   selection: Array,
 *   regionGroups: Array,
 *   activeRegionGroupId: string|null,
 *   layout: Array,
 *   interactionMode: string,
 *   interpolator: string,
 *   pitchPreservation: boolean
 * }}
 *
 * @example
 * function Editor() {
 *   const canvasRef = useRef(null);
 *   const { daw, tracks, isPlaying, currentTime, duration } =
 *     useAudioDAWState(canvasRef, { pixelsPerSecond: 60 });
 *
 *   return (
 *     <>
 *       <canvas ref={canvasRef} width={1000} height={400} />
 *       <button onClick={() => (isPlaying ? daw.stop() : daw.play())}>
 *         {isPlaying ? "Pause" : "Play"}
 *       </button>
 *       <span>{currentTime.toFixed(2)} / {duration.toFixed(2)}s</span>
 *       <ul>{tracks.map((t) => <li key={t.id}>{t.fileName}</li>)}</ul>
 *     </>
 *   );
 * }
 */
export function useAudioDAWState(canvasRef, options = {}) {
  const [tracks, setTracks] = useState([]);
  const [layout, setLayout] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [zoom, setZoom] = useState(options.pixelsPerSecond || 50);
  const [scrollTime, setScrollTime] = useState(0);
  const [selection, setSelection] = useState([]);
  const [regionGroups, setRegionGroups] = useState([]);
  const [activeRegionGroupId, setActiveRegionGroupId] = useState(null);
  const [interactionMode, setInteractionMode] = useState(
    options.interactionMode || "arrange"
  );
  const [interpolator, setInterpolator] = useState(options.interpolator || null);
  const [pitchPreservation, setPitchPreservation] = useState(
    options.preservePitch || false
  );

  const dawRef = useRef(null);

  const refreshTracks = useCallback(() => {
    if (dawRef.current) setTracks(dawRef.current.getTracks());
  }, []);

  const refreshRegions = useCallback(() => {
    if (!dawRef.current) return;
    setRegionGroups(dawRef.current.getRegionGroups());
    // A new array every time: the regions themselves are mutated in place, so
    // an equal reference would let React skip a render that did change.
    setSelection([...dawRef.current.getActiveRegions()]);
  }, []);

  // Built fresh each render on purpose — `useAudioDAW` reads these through a ref
  // rather than closing over them, so there is nothing to memoise and no stale
  // closure to guard against.
  const merged = {
    ...options,

    onTrackAdded: (...args) => {
      refreshTracks();
      options.onTrackAdded?.(...args);
    },
    onTrackRemoved: (...args) => {
      refreshTracks();
      options.onTrackRemoved?.(...args);
    },
    onTrackReplaced: (...args) => {
      refreshTracks();
      options.onTrackReplaced?.(...args);
    },
    onTrackOffsetChange: (...args) => {
      refreshTracks();
      options.onTrackOffsetChange?.(...args);
    },
    onTrackVolumeChange: (...args) => {
      refreshTracks();
      options.onTrackVolumeChange?.(...args);
    },
    onTrackPanChange: (...args) => {
      refreshTracks();
      options.onTrackPanChange?.(...args);
    },
    onTrackGainChange: (...args) => {
      refreshTracks();
      options.onTrackGainChange?.(...args);
    },
    onTrackCompressorChange: (...args) => {
      refreshTracks();
      options.onTrackCompressorChange?.(...args);
    },
    onTrackMuteChange: (...args) => {
      refreshTracks();
      options.onTrackMuteChange?.(...args);
    },
    onTrackSoloChange: (...args) => {
      // Solo is timeline-wide: one track being soloed changes what every other
      // track can be heard doing, so the whole list has to refresh.
      refreshTracks();
      options.onTrackSoloChange?.(...args);
    },
    onTrackLayoutChange: (next) => {
      setLayout(next);
      options.onTrackLayoutChange?.(next);
    },
    onTrackFrequencyRangeChange: (...args) => {
      refreshTracks();
      options.onTrackFrequencyRangeChange?.(...args);
    },
    onTrackColorChange: (...args) => {
      refreshTracks();
      options.onTrackColorChange?.(...args);
    },
    onTrackSpectrogramOpacityChange: (...args) => {
      refreshTracks();
      options.onTrackSpectrogramOpacityChange?.(...args);
    },
    onTrackSpectrogramVariantChange: (...args) => {
      refreshTracks();
      options.onTrackSpectrogramVariantChange?.(...args);
    },
    onLoadingStateChange: (...args) => {
      refreshTracks();
      options.onLoadingStateChange?.(...args);
    },

    onPlayStateChange: (playing) => {
      setIsPlaying(playing);
      options.onPlayStateChange?.(playing);
    },
    onTimeUpdate: (time) => {
      setCurrentTime(time);
      options.onTimeUpdate?.(time);
    },
    onDurationChange: (value) => {
      setDuration(value);
      options.onDurationChange?.(value);
    },
    onPlaybackSpeedChange: (speed) => {
      setPlaybackSpeed(speed);
      options.onPlaybackSpeedChange?.(speed);
    },
    onZoomChange: (value) => {
      setZoom(value);
      options.onZoomChange?.(value);
    },
    onScrollChange: (value) => {
      setScrollTime(value);
      options.onScrollChange?.(value);
    },
    onInteractionModeChange: (mode) => {
      setInteractionMode(mode);
      options.onInteractionModeChange?.(mode);
    },
    onInterpolatorChange: (id) => {
      setInterpolator(id);
      options.onInterpolatorChange?.(id);
    },
    onPitchPreservationChange: (enabled) => {
      setPitchPreservation(enabled);
      options.onPitchPreservationChange?.(enabled);
    },

    onSelectionChange: (sel) => {
      setSelection(sel);
      options.onSelectionChange?.(sel);
    },
    onRegionsChange: (...args) => {
      refreshRegions();
      options.onRegionsChange?.(...args);
    },
    // Drag ticks still refresh the state, so a region list follows the mouse.
    onRegionsDrag: (...args) => {
      refreshRegions();
      options.onRegionsDrag?.(...args);
    },
    onRegionGroupsChange: (groups) => {
      setRegionGroups(groups);
      options.onRegionGroupsChange?.(groups);
    },
    onActiveRegionGroupChange: (groupId) => {
      setActiveRegionGroupId(groupId);
      refreshRegions();
      options.onActiveRegionGroupChange?.(groupId);
    },
  };

  const daw = useAudioDAW(canvasRef, merged);
  dawRef.current = daw;

  // Seed from the instance once it exists: the constructor applies options the
  // DAW then never re-announces, so without this the first render's state can
  // disagree with the DAW it is describing.
  useEffect(() => {
    if (!daw) return;

    setTracks(daw.getTracks());
    setLayout(daw.getTrackLayout());
    setZoom(daw.getZoom());
    setDuration(daw.getDuration());
    setScrollTime(daw.getScrollTime());
    setPlaybackSpeed(daw.getPlaybackSpeed());
    setIsPlaying(daw.getIsPlaying());
    setCurrentTime(daw.getCurrentTime());
    setRegionGroups(daw.getRegionGroups());
    setActiveRegionGroupId(daw.getActiveRegionGroupId());
    setSelection([...daw.getActiveRegions()]);
    setInteractionMode(daw.getInteractionMode());
    setInterpolator(daw.getInterpolator());
    setPitchPreservation(daw.getPitchPreservation());
  }, [daw]);

  return {
    daw,
    tracks,
    layout,
    isPlaying,
    currentTime,
    duration,
    playbackSpeed,
    zoom,
    scrollTime,
    selection,
    regionGroups,
    activeRegionGroupId,
    interactionMode,
    interpolator,
    pitchPreservation,
  };
}

/**
 * Streaming health for tracks loaded from af.
 *
 * Polled rather than pushed, because the DAW doesn't announce either value —
 * they change continuously as blocks arrive, and a callback per block would be a
 * render per block. Polling is opt-in for the same reason: a host that isn't
 * drawing a buffering indicator shouldn't pay for one.
 *
 * @param {AudioDAW|null} daw
 * @param {number} [intervalMs=250] - poll period; 0 disables polling
 * @returns {{stalled: boolean, bufferedRanges: Array}}
 *
 * @example
 * const { stalled, bufferedRanges } = useAudioDAWBuffering(daw);
 * return stalled ? <Spinner /> : null;
 */
export function useAudioDAWBuffering(daw, intervalMs = 250) {
  const [stalled, setStalled] = useState(false);
  const [bufferedRanges, setBufferedRanges] = useState([]);

  useEffect(() => {
    if (!daw || !intervalMs) return undefined;

    const sample = () => {
      setStalled(daw.isStalled());
      setBufferedRanges(daw.getBufferedRanges());
    };

    sample();
    const timer = setInterval(sample, intervalMs);
    return () => clearInterval(timer);
  }, [daw, intervalMs]);

  return { stalled, bufferedRanges };
}

/**
 * Save and restore DAW state.
 *
 * `loadState` needs the audio back as well as the description of it — state is
 * JSON and carries no samples. Remote tracks re-fetch themselves from the
 * manifest URL in the state; local ones need their `File` handed back, which is
 * what `useAudioFileRegistry` is for.
 *
 * @param {AudioDAW|null} daw
 * @returns {{saveState: Function, loadState: Function, exportState: Function, importState: Function}}
 *
 * @example
 * const { saveState, loadState } = useAudioDAWPersistence(daw);
 * const { audioFiles, registerAudioFile } = useAudioFileRegistry();
 *
 * const save = () => localStorage.setItem("daw", JSON.stringify(saveState()));
 * const load = () => loadState(JSON.parse(localStorage.getItem("daw")), audioFiles);
 */
export function useAudioDAWPersistence(daw) {
  const saveState = useCallback(() => {
    if (!daw) throw new Error("DAW instance not available");
    return daw.getState();
  }, [daw]);

  const loadState = useCallback(
    async (state, audioFiles = {}) => {
      if (!daw) throw new Error("DAW instance not available");
      await daw.setState(state, audioFiles);
    },
    [daw]
  );

  const exportState = useCallback(() => JSON.stringify(saveState(), null, 2), [saveState]);

  const importState = useCallback(
    async (json, audioFiles = {}) => loadState(JSON.parse(json), audioFiles),
    [loadState]
  );

  return { saveState, loadState, exportState, importState };
}

/**
 * Hold on to the `File` objects behind locally-loaded tracks.
 *
 * Only needed for local files. Tracks added with `addRemoteTrack` carry their
 * manifest URL in the saved state and restore without help.
 *
 * @returns {{audioFiles: Object, registerAudioFile: Function, unregisterAudioFile: Function, clearAudioFiles: Function}}
 */
export function useAudioFileRegistry() {
  const [audioFiles, setAudioFiles] = useState({});

  const registerAudioFile = useCallback((trackId, audioFile) => {
    setAudioFiles((prev) => ({ ...prev, [trackId]: audioFile }));
  }, []);

  const unregisterAudioFile = useCallback((trackId) => {
    setAudioFiles((prev) => {
      const { [trackId]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearAudioFiles = useCallback(() => setAudioFiles({}), []);

  return { audioFiles, registerAudioFile, unregisterAudioFile, clearAudioFiles };
}
